import {
    MagnifyingGlass,
    ArrowDown,
    ArrowLineUpRight,
    ArrowsClockwise,
    ArrowsLeftRight,
    ArrowUp,
    Bell,
    BookmarkSimple,
    ChartBar,
    ClockCounterClockwise,
    CurrencyDollar,
    EnvelopeSimple,
    Factory,
    ListChecks,
    Gear,
    Handshake,
    House,
    Package,
    Receipt,
    ShoppingCart,
    Truck,
    TrendUpIcon,
} from '@phosphor-icons/react';
import type { LockedModuleKey } from '@diagon-alley/common';

export interface MenuItem {
    label: string;
    /** Optional for nested child items, which render indented without an icon. */
    icon?: React.ElementType;
    path: string;
    /** Nested sub-items (e.g. Plan / Track under a planning module). */
    children?: MenuItem[];
    /**
     * The plan module this destination belongs to. A brand whose `lockedModules` names it sees
     * the item under "Premium" in its section and is bounced off the route (LockedModuleGate).
     * Home has none: it is always open.
     */
    module?: LockedModuleKey;
}

export interface MenuGroup {
    label?: string;
    items: MenuItem[];
}

export const NAV_GROUPS: MenuGroup[] = [
    {
        items: [
            { label: 'Home', path: '/home', icon: House },
        ],
    },
    {
        label: 'Visibility',
        items: [
            { label: 'Demand', path: '/forecasts', icon: TrendUpIcon, module: 'visibility.demand' },
            { label: 'Sales', path: '/sales', icon: CurrencyDollar, module: 'visibility.sales' },
            // Partner-raised orders (docs/prds/sales-orders-v1.md); Purchase Orders keeps both sides as before.
            { label: 'Sales Orders', path: '/sales-orders', icon: Receipt, module: 'visibility.sales_orders' },
            // Shown only when b2b_config.enabled (see SideNavigationBar).
            {
                label: 'B2B Customers',
                path: '/b2b/customers',
                icon: Handshake,
                module: 'visibility.b2b_customers',
                children: [
                    { label: 'Customers', path: '/b2b/customers' },
                    { label: 'Price lists & schemes', path: '/b2b/pricing' },
                ],
            },
            { label: 'Inventory', path: '/inventory', icon: Package, module: 'visibility.inventory' },
            { label: 'Purchase Orders', path: '/purchase-orders', icon: ShoppingCart, module: 'visibility.purchase_orders' },
            { label: 'Production', path: '/production-planning', icon: Factory, module: 'visibility.production' },
            { label: 'Suppliers', path: '/suppliers', icon: Truck, module: 'visibility.suppliers' },
        ],
    },
    {
        label: 'Monitor',
        items: [
            { label: 'Alerts', path: '/alerts', icon: Bell, module: 'monitor.alerts' },
            { label: 'Reports', path: '/reports', icon: ChartBar, module: 'monitor.reports' },
            { label: 'Saved Reports', path: '/reports/library', icon: BookmarkSimple, module: 'monitor.saved_reports' },
            { label: 'Scheduled Emails', path: '/scheduled-emails', icon: EnvelopeSimple, module: 'monitor.saved_reports' },
        ],
    },
    {
        label: 'Plan',
        items: [
            { label: 'Replenishment', path: '/replenishment', icon: ArrowsClockwise, module: 'plan.replenishment' },
            { label: 'Distribution', path: '/distribution', icon: ArrowLineUpRight, module: 'plan.distribution' },
            { label: 'Rebalancing', path: '/rebalance', icon: ArrowsLeftRight, module: 'plan.rebalance' },
        ],
    },
    {
        label: 'Execute',
        items: [
            // The PO Builder ("Purchase Orders" at /procurement-orders) is being sunset —
            // purchase orders are authored from Orders and production orders from Production
            // Orders, so the tab was a third door onto the same rows. The route and container
            // stay for now: Production Orders still deep-links into them to raise a PO against
            // a material deficit (?workOrderId=&materialId=) and to open a pegged PO (?id=),
            // and neither flow has a home on Orders yet. Drop them, and this whole surface can
            // go with them.
            { label: 'Create Purchase Order', path: '/orders', icon: Package, module: 'execute.create_purchase_order' },
            { label: 'Shipments', path: '/shipments', icon: Truck, module: 'execute.shipments' },
            { label: 'Stock Transfers', path: '/stock-transfers', icon: ArrowsLeftRight, module: 'execute.stock_transfers' },
            { label: 'Payments', path: '/payments', icon: CurrencyDollar, module: 'execute.payments' },
            { label: 'Production Orders', path: '/work-orders', icon: Factory, module: 'execute.production_orders' },
            { label: 'CM Sites', path: '/cm-sites', icon: Factory, module: 'execute.cm_sites' },
            { label: 'Trace', path: '/trace', icon: MagnifyingGlass, module: 'execute.trace' },
        ],
    },
    {
        label: 'Warehouse Ops',
        items: [
            { label: 'Inbound', path: '/warehouse-ops/inbound', icon: ArrowDown, module: 'warehouse_ops' },
            { label: 'Outbound', path: '/warehouse-ops/outbound', icon: ArrowUp, module: 'warehouse_ops' },
        ],
    },
];

export const BOTTOM_ITEMS: MenuItem[] = [
    { label: 'Settings', path: '/settings', icon: Gear },
];

export const COMPANY_NAME = 'Reparo';

export const SIDEBAR_BG = '#ececec';
export const ACTIVE_BG = '#d9d9d9';
export const HOVER_BG = '#d3d3d3';

/**
 * A manufacturing site's navigation.
 *
 * Four destinations rather than the two the v0 portal had, and one departure: there is no
 * top-level Upload. Bulk entry is not a place a person goes — seeding opening balances is a
 * one-off act of onboarding, and pasting a day's receipts belongs inside Receive next to the
 * form it replaces. A tab for it invited a manufacturer to treat their whole month as an
 * import job.
 *
 * `available` gates a destination on its screen existing. A greyed item a person cannot reach
 * is better than a live one that lands on nothing, and the flag makes the remaining work
 * greppable rather than hidden in a router file.
 */
export interface VendorNavItem extends MenuItem {
    available: boolean;
}

/** The customer portal (B2B customer logins: distributors, retail chains, institutions). */
export const B2B_NAV_ITEMS: VendorNavItem[] = [
    { label: 'Home', path: '/portal/home', icon: House, available: true },
    { label: 'New order', path: '/portal/orders/new', icon: ShoppingCart, available: true },
    { label: 'My orders', path: '/portal/orders', icon: Receipt, available: true },
];

export const VENDOR_NAV_ITEMS: VendorNavItem[] = [
    { label: 'Home', path: '/vendor/home', icon: House, available: true },
    { label: 'Receive', path: '/vendor/receive', icon: ArrowDown, available: true },
    // The route slug stays /vendor/stock - a label rename must not break bookmarks.
    { label: 'Finished Goods', path: '/vendor/stock', icon: Package, available: true },
    // Raw and packaging material - named the way the floor names it.
    { label: 'RM/PM', path: '/vendor/rm-pm', icon: Factory, available: true },
    // Next to RM/PM on purpose: this is the document that says what that material is FOR, and
    // it is the first thing somebody checks when a shipment and a run do not add up.
    { label: 'Bill of Materials', path: '/vendor/bom', icon: ListChecks, available: true },
    { label: 'Production Orders', path: '/vendor/production-orders', icon: ShoppingCart, available: true },
    { label: 'Dispatch', path: '/vendor/dispatch', icon: Truck, available: true },
    { label: 'History', path: '/vendor/history', icon: ClockCounterClockwise, available: true },
    { label: 'Trace', path: '/vendor/trace', icon: MagnifyingGlass, available: true },
];
